export const UK = { red: '#C8102E', blue: '#012169', white: '#FFFFFF', gold: '#E6A817' };

export const UNIT_LABELS: Record<string, string> = {
  residential: 'Residential', commercial: 'Commercial', shop: 'Shop',
  parking: 'Parking', storage: 'Storage', garage: 'Garage',
};

export const TENANT_FILTERS = ['All', 'Active', 'No Active Lease', 'Multi-Property', 'No RTR', 'RTR Expiring'] as const;

export const HOUSING_REQ_DEFAULTS = {
  ground_floor_only: false, wheelchair_access: false, lift_required: false, no_stairs: false,
  near_hospital: false, near_transport: false, quiet_area: false, parking_required: false,
  pet_friendly: false, furnished_only: false, max_budget: '', preferred_areas: '',
};

export type TenantFormState = {
  full_name: string; email: string; phone: string; property_address: string;
  move_in_date: string; move_out_date: string; room_number: string; occupants: string;
  right_to_rent_checked: string; right_to_rent_doc_type: string; right_to_rent_expiry: string; right_to_rent_share_code: string;
  guarantor_name: string; guarantor_phone: string; guarantor_email: string; guarantor_relationship: string; guarantor_address: string;
  emergency_contact_name: string; emergency_contact_phone: string; emergency_contact_relationship: string;
  housing_requirements: typeof HOUSING_REQ_DEFAULTS;
};

export const DEFAULT_TENANT_FORM: TenantFormState = {
  full_name: '', email: '', phone: '', property_address: '', move_in_date: '', move_out_date: '',
  room_number: '', occupants: '', right_to_rent_checked: '', right_to_rent_doc_type: '',
  right_to_rent_expiry: '', right_to_rent_share_code: '',
  guarantor_name: '', guarantor_phone: '', guarantor_email: '', guarantor_relationship: '', guarantor_address: '',
  emergency_contact_name: '', emergency_contact_phone: '', emergency_contact_relationship: '',
  housing_requirements: { ...HOUSING_REQ_DEFAULTS },
};

export type RefFormState = {
  credit_check: string; employment_check: string; landlord_reference: string;
  right_to_rent_verified: string; deposit_received: string; notes: string;
};

export const DEFAULT_REF_FORM: RefFormState = {
  credit_check: 'pending', employment_check: 'pending', landlord_reference: 'pending',
  right_to_rent_verified: 'pending', deposit_received: 'pending', notes: '',
};

const toDateStr = (v: unknown) => v ? String(v).split('T')[0] : '';

export function tenantToForm(t: Record<string, any>): TenantFormState {
  const req = { ...HOUSING_REQ_DEFAULTS, ...(t.housing_requirements || {}) };
  if (req.max_budget) req.max_budget = String(req.max_budget);
  return {
    ...DEFAULT_TENANT_FORM,
    full_name: t.full_name || '',
    email: t.email || '',
    phone: t.phone || '',
    property_address: t.property_address || '',
    move_in_date: toDateStr(t.move_in_date),
    move_out_date: toDateStr(t.move_out_date),
    room_number: t.room_number || '',
    occupants: t.occupants || '',
    right_to_rent_checked: toDateStr(t.right_to_rent_checked),
    right_to_rent_doc_type: t.right_to_rent_doc_type || '',
    right_to_rent_expiry: toDateStr(t.right_to_rent_expiry),
    right_to_rent_share_code: t.right_to_rent_share_code || '',
    guarantor_name: t.guarantor_name || '',
    guarantor_phone: t.guarantor_phone || '',
    guarantor_email: t.guarantor_email || '',
    guarantor_relationship: t.guarantor_relationship || '',
    guarantor_address: t.guarantor_address || '',
    emergency_contact_name: t.emergency_contact_name || '',
    emergency_contact_phone: t.emergency_contact_phone || '',
    emergency_contact_relationship: t.emergency_contact_relationship || '',
    housing_requirements: req,
  };
}

export function serializeTenantForm(form: TenantFormState, isEdit: boolean) {
  const payload: Record<string, any> = { ...form };
  if (isEdit && form.housing_requirements) {
    const hr: Record<string, unknown> = { ...form.housing_requirements };
    if (hr.max_budget) hr.max_budget = Number(hr.max_budget) || '';
    payload.housing_requirements = hr;
  } else {
    delete payload.housing_requirements;
  }
  return payload;
}

export const fmt = (d: string) => d ? new Date(d).toLocaleDateString('en-GB') : '—';
export const ini = (name: string) => name ? name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase() : '?';

export const effectiveStatus = (t: any) =>
  (t.active_lease_count > 0 || t.active_lease_id) ? 'active' : 'former';

export function tenantLeaseStatus(t: any): 'active' | 'expiring_soon' | 'notice_given' | 'expired' | 'former' {
  const raw = String(t.active_lease_status || t.computed_status || t.status || '').toLowerCase();
  const leaseEndMs = t.lease_end_date ? new Date(t.lease_end_date).getTime() : NaN;
  const hasFutureLeaseSignal = !!t.active_rent && !Number.isNaN(leaseEndMs) && leaseEndMs >= (Date.now() - 86400000);
  if ((raw === 'former' || raw === 'ended') && hasFutureLeaseSignal) {
    const days = Math.floor((leaseEndMs - Date.now()) / 86400000);
    if (!Number.isNaN(days) && days <= 60) return 'expiring_soon';
    return 'active';
  }
  if (raw === 'notice_given') return 'notice_given';
  if (raw === 'expiring_soon') return 'expiring_soon';
  if (raw === 'expired') return 'expired';
  if (raw === 'ended' || raw === 'former') return 'former';
  if (raw === 'active') {
    if (t.lease_end_date) {
      const days = Math.floor((new Date(t.lease_end_date).getTime() - Date.now()) / 86400000);
      if (!Number.isNaN(days) && days < 0) return 'expired';
      if (!Number.isNaN(days) && days <= 60) return 'expiring_soon';
    }
    return 'active';
  }
  return t.active_lease_id ? 'active' : 'former';
}

export function tenantAccent(t: any) {
  if (effectiveStatus(t) === 'active' && !t.right_to_rent_checked) return { color: UK.red, label: 'RTR Missing' };
  if (effectiveStatus(t) === 'active' && t.right_to_rent_expiry) {
    const days = Math.floor((new Date(t.right_to_rent_expiry).getTime() - Date.now()) / 86400000);
    if (days >= 0 && days <= 30) return { color: UK.gold, label: 'RTR Expiring' };
    if (days < 0) return { color: UK.red, label: 'RTR Expired' };
  }
  const st = tenantLeaseStatus(t);
  if (st === 'expiring_soon' || st === 'notice_given') return { color: UK.gold, label: 'Lease Expiring' };
  if (st === 'active') return { color: '#16a34a', label: 'Active' };
  return { color: '#94a3b8', label: 'No Lease' };
}

export function rtrBadge(t: any) {
  if (!t.right_to_rent_checked) return { label: 'Not checked', bg: '#f1f5f9', color: '#6b7280' };
  if (t.right_to_rent_expiry) {
    const days = Math.floor((new Date(t.right_to_rent_expiry).getTime() - Date.now()) / 86400000);
    if (days < 0) return { label: 'RTR Expired', bg: '#fee2e2', color: UK.red };
    if (days <= 30) return { label: `RTR ${days}d left`, bg: '#fef3c7', color: '#b45309' };
  }
  return { label: 'RTR Valid', bg: '#dcfce7', color: '#16a34a' };
}

export function leaseLine(l: any) {
  const parts = [];
  if (l.room_number) parts.push(l.room_number);
  parts.push(l.property_address);
  const unit = l.unit_type && l.unit_type !== 'residential' ? ` (${UNIT_LABELS[l.unit_type] || l.unit_type})` : '';
  return parts.join(', ') + unit;
}
